import { useCallback, useEffect, useState } from 'react';

import { FALLBACK_WINDOW_DAYS } from '../lib/queue';
import { supabase } from '../lib/supabase';

/**
 * Queue activity for the same window the Drafted filter is showing: two small bars a day, what
 * arrived and what went out.
 *
 * ## The span follows the filter
 *
 * `spanDays` comes from `windowDays(windowKey)` in lib/queue, so choosing "Last 3 days" draws three
 * bars and "Last 14 days" draws fourteen - the chart and the list underneath it always describe the
 * same stretch of time. "Any time" has no start, so it falls back to a fortnight rather than trying
 * to draw every day since the first draft.
 *
 * The bars are a share of the busiest day *in view*, so a quiet three days still reads as a shape
 * rather than as three stubs at the bottom of a scale set by a busier fortnight.
 *
 * ## Where the numbers come from, and why not `deliveries`
 *
 * Both series are read from `drafts`, because that is where the queue's own numbers come from. The
 * "posted" bar counts `state = 'sent'` by `updated_at`, which is exactly what the Posted toggle on
 * this page counts, so the chart and the toggle cannot disagree. It used to count
 * `deliveries.posted_at`, which is the intended record, but that table was empty while six drafts
 * were marked sent - so the bar read 0 next to a toggle reading 6.
 *
 * ## Why it is HTML rather than SVG
 *
 * The first version was an inline `<svg>` with a fixed viewBox scaled to the container, and the axis
 * labels came out about twice their intended size: an SVG `font-size` is in user units and is scaled
 * along with everything else. HTML gives real pixels at every width, and a bar is a span with a
 * height in px, which is all this chart ever needed.
 */

/** Plot height in px. Bars are a share of the busiest day rather than of a round number. */
const PLOT_PX = 68;

interface Day {
  /** 'Fri 18' - short enough that a fortnight of them fits without turning. */
  label: string;
  /** The full description, for the tooltip and for screen readers. */
  title: string;
  drafted: number;
  posted: number;
}

/** Local calendar day key, so the buckets follow the operator's day rather than UTC. */
function dayKey(date: Date): string {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

function spanLabel(span: number): string {
  return span === 1 ? 'Last 1 day' : `Last ${span} days`;
}

interface Props {
  /** How many days to draw. Comes from the Drafted filter. */
  spanDays?: number;
  /** Changes when the queue reloads, so the chart follows the list rather than the clock. */
  refreshToken?: number;
}

export default function QueueTimeline({ spanDays = FALLBACK_WINDOW_DAYS, refreshToken = 0 }: Props) {
  const [days, setDays] = useState<Day[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const span = Math.max(1, Math.floor(spanDays));

  const load = useCallback(async () => {
    const now = new Date();
    const buckets: Day[] = [];
    const index = new Map<string, number>();

    for (let offset = span - 1; offset >= 0; offset -= 1) {
      const date = new Date(now.getTime() - offset * 86_400_000);
      index.set(dayKey(date), buckets.length);
      buckets.push({
        label: date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric' }),
        title: date.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short' }),
        drafted: 0,
        posted: 0,
      });
    }

    // Midnight on the earliest day shown, so every bar is a whole day rather than a part of one.
    const start = new Date(now.getTime() - (span - 1) * 86_400_000);
    start.setHours(0, 0, 0, 0);
    const since = start.toISOString();

    const [drafted, posted] = await Promise.all([
      supabase.from('drafts').select('created_at').gte('created_at', since).limit(2000),
      supabase
        .from('drafts')
        .select('updated_at')
        .eq('state', 'sent')
        .gte('updated_at', since)
        .limit(2000),
    ]);

    const problem = drafted.error ?? posted.error;
    if (problem) {
      setError(problem.message);
      return;
    }
    setError(null);

    for (const row of drafted.data ?? []) {
      const position = index.get(dayKey(new Date(row.created_at as string)));
      if (position !== undefined) buckets[position].drafted += 1;
    }
    for (const row of posted.data ?? []) {
      const position = index.get(dayKey(new Date(row.updated_at as string)));
      if (position !== undefined) buckets[position].posted += 1;
    }

    setDays(buckets);
  }, [span]);

  useEffect(() => {
    void load();
  }, [load, refreshToken]);

  const draftedTotal = days?.reduce((sum, day) => sum + day.drafted, 0) ?? 0;
  const postedTotal = days?.reduce((sum, day) => sum + day.posted, 0) ?? 0;
  const peak = days ? Math.max(1, ...days.map((day) => Math.max(day.drafted, day.posted))) : 1;

  /** A bar is never thinner than 3px, so a day with one reply still reads as a day with one. */
  function height(count: number): number {
    return count === 0 ? 0 : Math.max(3, Math.round((count / peak) * PLOT_PX));
  }

  // One template for the bars and one for the labels, so a bar and its day line up at any span.
  const columns = { gridTemplateColumns: `repeat(${span}, minmax(0, 1fr))` };

  return (
    <div className="timeline">
      <div className="timeline-head">
        <span className="timeline-title">{spanLabel(span)}</span>
        <span className="timeline-key">
          <i className="swatch drafted" aria-hidden="true" />
          {draftedTotal} drafted
          <i className="swatch posted" aria-hidden="true" />
          {postedTotal} posted
        </span>
      </div>

      {error ? <p className="error small">{error}</p> : null}
      {!days && !error ? <p className="muted small">Loading activity…</p> : null}

      {days ? (
        <div
          className="timeline-chart"
          role="img"
          aria-label={`Queue activity for the ${spanLabel(span).toLowerCase()}: ${draftedTotal} drafts created, ${postedTotal} marked posted.`}
        >
          {/* PLOT_PX for the bars plus headroom for the number above the tallest one. */}
          <div className="timeline-plot" style={{ height: PLOT_PX + 20, ...columns }}>
            {days.map((day, position) => (
              <div
                className="timeline-day"
                key={`${day.label}-${position}`}
                title={`${day.title}: ${day.drafted} drafted, ${day.posted} posted`}
              >
                <span className="timeline-pair">
                  {day.drafted > 0 ? (
                    <span className="timeline-bar-wrap">
                      <span className="timeline-value">{day.drafted}</span>
                      <i className="bar drafted" style={{ height: height(day.drafted) }} />
                    </span>
                  ) : null}
                  {day.posted > 0 ? (
                    <span className="timeline-bar-wrap">
                      <span className="timeline-value">{day.posted}</span>
                      <i className="bar posted" style={{ height: height(day.posted) }} />
                    </span>
                  ) : null}
                </span>
              </div>
            ))}
          </div>

          <div className="timeline-ticks" style={columns}>
            {days.map((day, position) => (
              <span className="timeline-tick" key={`${day.label}-tick-${position}`}>
                {day.label}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {days && draftedTotal === 0 && postedTotal === 0 ? (
        <p className="muted small">
          Nothing drafted or posted in the {spanLabel(span).toLowerCase()}.
        </p>
      ) : null}
    </div>
  );
}
